
import React from 'react';
import { SimulationConfig, BallStyle, EscapeColorMode } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';

interface VisualStylesSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const VisualStylesSection: React.FC<VisualStylesSectionProps> = ({ config, setConfig }) => {
  const ballStyles: BallStyle[] = ['SHINY', 'NEON', 'CHROME', 'MATTE', 'GHOST'];
  const ringPatterns: EscapeColorMode[] = ['RAINBOW', 'ALTERNATING', 'GRADIENT', 'UNIFORM', 'INDIVIDUAL'];

  const ColorInput = ({ label, value, onChange }: { label: string, value: string, onChange: (v: string) => void }) => (
    <ControlGroup label={label}>
        <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: value }} />
            <input type="color" value={value} onChange={e => onChange(e.target.value)} className="flex-1 h-8 opacity-0 absolute cursor-pointer" />
            <input type="text" value={value} onChange={e => onChange(e.target.value)} className={`flex-1 px-2 py-1 text-[9px] font-mono border rounded-lg ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`} />
        </div>
    </ControlGroup>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* NEW: DEDICATED BORDER SECTION AT THE TOP */}
      <SectionLabel icon="fa-square" darkMode={config.darkMode}>Container & Borders</SectionLabel>
      <div className="flex flex-col gap-3 p-4 rounded-3xl border-2 bg-yellow-500/10 border-yellow-500/30 shadow-lg shadow-yellow-500/5">
        <SmartInput 
            label="Border Thickness" 
            min={1} max={50} 
            value={config.containerLineWidth || 4} 
            onChange={v => setConfig(p => ({...p, containerLineWidth: v}))} 
            darkMode={config.darkMode} 
        />
        <ColorInput 
            label="Border Color" 
            value={config.borderColor || '#1a1a1a'} 
            onChange={v => setConfig(p => ({...p, borderColor: v}))} 
        />
        <ColorInput 
            label="Background Color" 
            value={config.outerColor} 
            onChange={v => setConfig(p => ({...p, outerColor: v}))} 
        />
      </div>

      <SectionLabel icon="fa-palette" darkMode={config.darkMode}>Color & FX Studio</SectionLabel>
      
      <div className="p-4 rounded-3xl border bg-indigo-500/5 border-indigo-500/10 mb-2">
        <div className="flex items-center justify-between mb-4">
            <span className="text-[11px] font-black uppercase text-indigo-400">Master RGB Engine</span>
            <Toggle active={config.rgbLighting} onClick={() => setConfig(p => ({...p, rgbLighting: !p.rgbLighting}))} color="bg-indigo-600" />
        </div>
        
        <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="flex items-center justify-between p-2 rounded-xl bg-black/5 border border-white/5">
                <span className="text-[8px] font-black uppercase opacity-60">Ball RGB</span>
                <Toggle active={config.useBallRGB} onClick={() => setConfig(p => ({...p, useBallRGB: !p.useBallRGB}))} />
            </div>
            <div className="flex items-center justify-between p-2 rounded-xl bg-black/5 border border-white/5">
                <span className="text-[8px] font-black uppercase opacity-60">FX RGB</span>
                <Toggle active={config.useFXRGB} onClick={() => setConfig(p => ({...p, useFXRGB: !p.useFXRGB}))} />
            </div>
            <div className="flex items-center justify-between p-2 rounded-xl bg-black/5 border border-white/5">
                <span className="text-[8px] font-black uppercase opacity-60">Trail RGB</span>
                <Toggle active={config.useTrailRGB} onClick={() => setConfig(p => ({...p, useTrailRGB: !p.useTrailRGB}))} />
            </div>
            <div className="flex items-center justify-between p-2 rounded-xl bg-black/5 border border-white/5">
                <span className="text-[8px] font-black uppercase opacity-60">Borders RGB</span>
                <Toggle active={config.useContainerRGB} onClick={() => setConfig(p => ({...p, useContainerRGB: !p.useContainerRGB}))} />
            </div>
        </div>
      </div>

      <SectionLabel icon="fa-circle" darkMode={config.darkMode}>Sphere Studio</SectionLabel>
      <div className={`grid grid-cols-3 gap-2 p-1 w-full rounded-xl mb-2 ${config.darkMode ? 'bg-neutral-800' : 'bg-neutral-100'}`}>
        {ballStyles.map(s => (
          <button key={s} onClick={() => setConfig(prev => ({ ...prev, ballStyle: s }))} className={`text-[8px] font-black py-2 rounded-lg transition-all ${config.ballStyle === s ? (config.darkMode ? 'bg-neutral-700 text-white' : 'bg-white text-neutral-900 shadow-sm') : 'text-neutral-500'}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10">
        <ColorInput label="Primary Sphere" value={config.ball1Color} onChange={v => setConfig(p => ({...p, ball1Color: v}))} />
        <ColorInput label="Secondary Sphere" value={config.ball2Color} onChange={v => setConfig(p => ({...p, ball2Color: v}))} />
      </div>

      <SectionLabel icon="fa-bullseye" darkMode={config.darkMode}>Ring Pattern Studio</SectionLabel>
      <div className="flex flex-col gap-3 p-4 rounded-3xl border bg-blue-500/5 border-blue-500/10 mb-2">
        <ControlGroup label="Pattern Mode">
            <select value={config.escapeColorMode} onChange={e => setConfig(p => ({...p, escapeColorMode: e.target.value as EscapeColorMode}))} className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}>
                {ringPatterns.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
        </ControlGroup>
        
        {(config.escapeColorMode === 'ALTERNATING' || config.escapeColorMode === 'GRADIENT') && (
            <div className="grid grid-cols-2 gap-3">
                <ColorInput label="Pattern Start" value={config.escapeColorA} onChange={v => setConfig(p => ({...p, escapeColorA: v}))} />
                <ColorInput label="Pattern End" value={config.escapeColorB} onChange={v => setConfig(p => ({...p, escapeColorB: v}))} />
            </div>
        )}
        
        {config.escapeColorMode === 'UNIFORM' && (
            <ColorInput label="Ring Color" value={config.escapeUniformColor} onChange={v => setConfig(p => ({...p, escapeUniformColor: v}))} />
        )}
      </div>

      <SectionLabel icon="fa-vector-square" darkMode={config.darkMode}>Inner World Colors</SectionLabel>
      <div className="grid grid-cols-2 gap-3 p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10">
        <ColorInput label="Container Inner" value={config.innerColor} onChange={v => setConfig(p => ({...p, innerColor: v}))} />
        <ColorInput label="Title Text" value={config.titleColor} onChange={v => setConfig(p => ({...p, titleColor: v}))} />
        <ColorInput label="FX Waves" value={config.collisionWaveColor} onChange={v => setConfig(p => ({...p, collisionWaveColor: v}))} />
        <ColorInput label="Blast Shards" value={config.blastColor} onChange={v => setConfig(p => ({...p, blastColor: v}))} />
      </div>
    </div>
  );
};
